import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import type { EmbedConfigDto } from '../lib/types';
import {
  Badge, Button, Card, Checkbox, EmptyState, Field, IconButton, Input, Select, Spinner,
} from '../components/ui';
import { ShareLinks } from '../builder/ShareLinks';

export function Share() {
  const { formId = '' } = useParams();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const form = useQuery({ queryKey: ['form', formId], queryFn: () => api.forms.get(formId) });
  const embeds = useQuery({
    queryKey: ['embeds', formId],
    queryFn: () => api.embeds.list(formId),
  });

  const create = useMutation({
    mutationFn: (input: {
      name: string; mode: string; renderMode: string;
      requireSignedToken: boolean; origins: string[];
    }) => api.embeds.create(formId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embeds', formId] });
      setCreating(false);
    },
  });

  if (form.isLoading) return <div className="p-8"><Spinner /></div>;

  const published = form.data?.publishedVersion;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      {!published && (
        <Card className="border-[var(--ff-warning)] bg-[var(--ff-warning-soft)] p-4">
          <p className="text-sm font-medium text-[var(--ff-warning)]">
            This form is not published yet.
          </p>
          <p className="mt-1 text-sm">
            You can configure embeds now, but they will refuse to render until you publish.
          </p>
        </Card>
      )}

      {published && (
        <Card className="p-4">
          <h2 className="mb-1 text-sm font-semibold">Direct link</h2>
          <p className="mb-3 text-xs text-[var(--ff-text-muted)]">
            Version {published.number} · published {new Date(published.publishedUtc).toLocaleString()}
          </p>
          <CopyRow value={`${location.origin}/f/${formId}`} />
          <p className="mt-2 text-xs text-[var(--ff-text-muted)]">
            Embed URL: <code>{`${location.origin}/e?f=${formId}`}</code>
          </p>
        </Card>
      )}

      <ShareLinks formId={formId} />

      <div className="flex items-center gap-3">
        <div>
          <h2 className="text-sm font-semibold">Embeds</h2>
          <p className="text-xs text-[var(--ff-text-muted)]">
            One form can have several embeds with different behaviour and different allowed origins.
          </p>
        </div>
        <Button className="ml-auto" variant="primary" onClick={() => setCreating(true)}>
          New embed
        </Button>
      </div>

      {creating && (
        <CreateEmbed
          busy={create.isPending}
          error={create.error instanceof ApiError ? create.error.message : null}
          onCancel={() => setCreating(false)}
          onCreate={(input) => create.mutate(input)}
        />
      )}

      {embeds.isLoading && <Spinner />}

      {embeds.data?.data.length === 0 && !creating && (
        <Card>
          <EmptyState
            title="No embeds configured"
            description="An embed is a named configuration — mode, render style, and the list of origins allowed to frame this form."
            action={<Button variant="primary" onClick={() => setCreating(true)}>Create one</Button>}
          />
        </Card>
      )}

      {embeds.data?.data.map((embed) => (
        <EmbedCard key={embed.id} embed={embed} formId={formId} />
      ))}
    </div>
  );
}

function CreateEmbed({
  busy, error, onCreate, onCancel,
}: {
  busy: boolean;
  error: string | null;
  onCreate: (input: {
    name: string; mode: string; renderMode: string;
    requireSignedToken: boolean; origins: string[];
  }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('inline');
  const [renderMode, setRenderMode] = useState('iframe');
  const [signed, setSigned] = useState(false);
  const [origins, setOrigins] = useState('');

  return (
    <Card className="p-4">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onCreate({
            name: name.trim() || 'Embed',
            mode, renderMode, requireSignedToken: signed,
            origins: origins.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean),
          });
        }}
      >
        {error && (
          <p role="alert" className="text-sm text-[var(--ff-danger)]">{error}</p>
        )}

        <Field label="Name" hint="For your own reference, e.g. “Pricing page inline”.">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Mode">
            <Select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="inline">Inline</option>
              <option value="modal">Modal</option>
              <option value="slideover">Slide-over</option>
              <option value="sidetab">Side tab</option>
            </Select>
          </Field>

          <Field
            label="Isolation"
            hint={renderMode === 'iframe'
              ? 'The host page cannot read answers.'
              : 'The host page gets DOM access. Only for origins you trust.'}
          >
            <Select value={renderMode} onChange={(e) => setRenderMode(e.target.value)}>
              <option value="iframe">Iframe (recommended)</option>
              <option value="shadow">Shadow DOM</option>
            </Select>
          </Field>
        </div>

        <Field
          label="Allowed origins"
          hint="One per line. https://app.partner.com, or https://*.partner.com for one subdomain level. Leave empty and the embed is refused everywhere."
        >
          <Input
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            placeholder="https://app.partner.com"
            className="font-mono text-xs"
          />
        </Field>

        <Checkbox
          label="Require a signed token"
          description="The partner's server mints a short-lived HMAC token. Lets you identify the respondent and prefill trusted data without shipping a key to the browser."
          checked={signed}
          onChange={setSigned}
        />

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create embed'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

function EmbedCard({ embed, formId }: { embed: EmbedConfigDto; formId: string }) {
  const queryClient = useQueryClient();
  const [newOrigin, setNewOrigin] = useState('');
  const [originError, setOriginError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const addOrigin = useMutation({
    mutationFn: (origin: string) => api.embeds.addOrigin(embed.id, origin),
    onSuccess: () => {
      setNewOrigin('');
      setOriginError(null);
      queryClient.invalidateQueries({ queryKey: ['embeds', formId] });
    },
    onError: (err) => setOriginError(
      err instanceof ApiError ? err.message : 'Could not add that origin.'),
  });

  const mint = useMutation({
    mutationFn: () => api.embeds.mintToken(embed.id, {
      subject: 'demo_user_1',
      origin: embed.origins[0] ?? location.origin,
      claims: { email: 'jane@acme.com', plan: 'enterprise' },
    }),
    onSuccess: (result) => setToken(result.token),
  });

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-medium">{embed.name}</span>
        <Badge tone="primary">{embed.mode}</Badge>
        <Badge>{embed.renderMode}</Badge>
        {embed.requireSignedToken && <Badge tone="warning">signed token</Badge>}
        {embed.origins.length === 0 && <Badge tone="danger">no origins — will refuse</Badge>}
      </div>

      <h3 className="mb-1 text-xs font-semibold">Snippet</h3>
      <CopyRow value={embed.snippet} multiline />

      <h3 className="mt-4 mb-1 text-xs font-semibold">Allowed origins</h3>
      <p className="mb-2 text-xs text-[var(--ff-text-muted)]">
        Deny by default. Any origin not listed here gets a 403 and a console message naming
        itself, so the integrating developer can diagnose it without filing a ticket.
      </p>

      {embed.origins.length > 0 && (
        <ul className="mb-2 space-y-1">
          {embed.origins.map((origin) => (
            <li key={origin}
                className="flex items-center gap-2 rounded border border-[var(--ff-border)]
                           px-2 py-1 font-mono text-xs">
              {origin}
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); if (newOrigin.trim()) addOrigin.mutate(newOrigin.trim()); }}
      >
        <Input
          value={newOrigin}
          onChange={(e) => setNewOrigin(e.target.value)}
          placeholder="https://app.partner.com"
          aria-label={`Add an allowed origin to ${embed.name}`}
          className="font-mono text-xs"
        />
        <Button type="submit" disabled={addOrigin.isPending}>Add</Button>
      </form>
      {originError && (
        <p role="alert" className="mt-1 text-xs text-[var(--ff-danger)]">{originError}</p>
      )}

      {embed.requireSignedToken && (
        <div className="mt-4 rounded-md border border-[var(--ff-border)] p-3">
          <h3 className="mb-1 text-xs font-semibold">Signed token</h3>
          <p className="mb-2 text-xs text-[var(--ff-text-muted)]">
            In production your server mints this with the shared secret. This button is here
            so you can test the flow.
          </p>
          <Button onClick={() => mint.mutate()} disabled={mint.isPending}>
            {mint.isPending ? 'Minting…' : 'Mint a test token'}
          </Button>
          {token && <div className="mt-2"><CopyRow value={token} multiline /></div>}
        </div>
      )}
    </Card>
  );
}

function CopyRow({ value, multiline }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked in some contexts; the text is selectable either way.
    }
  }

  return (
    <div className="flex items-start gap-2">
      <pre className={`min-w-0 flex-1 overflow-x-auto rounded-md bg-[var(--ff-surface-sunken)]
                       p-2.5 font-mono text-[11px] leading-relaxed
                       ${multiline ? 'whitespace-pre' : 'whitespace-nowrap'}`}>
        {value}
      </pre>
      <IconButton label={copied ? 'Copied' : 'Copy to clipboard'} onClick={copy}>
        {copied ? '✓' : (
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor"
               strokeWidth="1.5" aria-hidden="true">
            <rect x="5" y="5" width="9" height="9" rx="1.5" />
            <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
          </svg>
        )}
      </IconButton>
    </div>
  );
}
